import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Scissors, Printer } from 'lucide-react';
import nurseryMaterialService from '../../services/nurseryMaterial';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz, getTodayInTz } from '../../utils/timezone';
import { formatQuantity, materialSource, seedsInBundle } from '../../utils/nurseryMaterial';
import {
  MATERIAL_KIND_LABELS, MATERIAL_STATUS_LABELS, SEED_TEST_METHOD_LABELS,
} from '../../types/nurseryMaterial';
import type { MaterialDetail as Material, MaterialEventType, SeedTestMethod } from '../../types/nurseryMaterial';

/**
 * /nursery/material/:id — one bundle: what is left of it, where it came from,
 * its germination tests if it is seed, and every change to it (propagation
 * plan, Phase 1). A bundle is counted, or written off with a reason; it is
 * never deleted.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

const EVENT_LABELS: Record<MaterialEventType, string> = {
  collected: 'Collected', adjust: 'Counted', draw: 'Drawn', write_off: 'Written off', reopen: 'Found more',
};

function MaterialDetail() {
  const { id } = useParams<{ id: string }>();
  const materialId = Number(id);
  const orgTz = useOrgTimezone();
  const today = getTodayInTz(orgTz);
  const { hasAccess: canOperate } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const [m, setM] = useState<Material | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'count' | 'write_off' | 'test' | 'edit' | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // count / write off
  const [remaining, setRemaining] = useState('');
  const [reason, setReason] = useState('');
  // germination test
  const [testedOn, setTestedOn] = useState(today);
  const [method, setMethod] = useState<SeedTestMethod>('paper_towel');
  const [tested, setTested] = useState('');
  const [germinated, setGerminated] = useState('');
  const [days, setDays] = useState('');
  // details
  const [useBy, setUseBy] = useState('');
  const [certScheme, setCertScheme] = useState('');
  const [certNumber, setCertNumber] = useState('');
  const [stratifyEnd, setStratifyEnd] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryMaterialService.get(materialId);
      if (res.success) setM(res.data);
      else setError(errorMessage(res, 'Material not found'));
    } catch (e) {
      setError(errorMessage(e, 'Material not found'));
    }
  }, [materialId]);

  useEffect(() => { load(); }, [load]);

  const open = (next: typeof mode) => {
    if (!m) return;
    setFormError(null);
    setReason('');
    if (next === 'count') setRemaining(String(Number(m.remaining)));
    if (next === 'edit') {
      setUseBy(m.use_by?.slice(0, 10) ?? '');
      setCertScheme(m.certificate_scheme ?? '');
      setCertNumber(m.certificate_number ?? '');
      setStratifyEnd(m.stratify_end?.slice(0, 10) ?? '');
      setNotes(m.notes ?? '');
    }
    setMode(next);
  };

  const run = async (call: () => Promise<{ success: boolean }>) => {
    setBusy(true);
    setFormError(null);
    try {
      const res = await call();
      if (!res.success) { setFormError(errorMessage(res, 'Could not save')); return; }
      setMode(null);
      load();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const saveCount = () => {
    const n = Number(remaining);
    if (remaining.trim() === '' || !(n >= 0)) { setFormError('Enter what is left, 0 or more'); return; }
    if (!reason.trim()) { setFormError('Say why the count changed'); return; }
    run(() => nurseryMaterialService.adjust(materialId, n, reason.trim()));
  };
  const saveWriteOff = () => {
    if (!reason.trim()) { setFormError('Say why it is written off'); return; }
    run(() => nurseryMaterialService.writeOff(materialId, reason.trim()));
  };
  const saveTest = () => {
    const t = Number(tested);
    const g = Number(germinated);
    if (!Number.isInteger(t) || t < 1) { setFormError('Enter how many seeds were tested'); return; }
    if (!Number.isInteger(g) || g < 0 || g > t) { setFormError('Germinated is a whole number, no more than were tested'); return; }
    run(() => nurseryMaterialService.addTest(materialId, {
      tested_on: testedOn, method, seeds_tested: t, seeds_germinated: g,
      days_to_germinate: days.trim() === '' ? null : Number(days),
    }));
  };
  const saveDetails = () => run(() => nurseryMaterialService.update(materialId, {
    use_by: useBy || null,
    certificate_scheme: certScheme.trim() || null,
    certificate_number: certNumber.trim() || null,
    notes: notes.trim() || null,
    ...(m?.kind === 'seed' ? { stratify_end: stratifyEnd || null } : {}),
  }));

  if (error) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div></div>;
  if (!m) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded-lg animate-pulse" /></div>;

  const isOpen = m.status === 'open';
  const pastUseBy = isOpen && m.use_by != null && m.use_by.slice(0, 10) < today;
  const seeds = seedsInBundle(m);
  const date = (d: string | null) => (d ? formatLocalDateInTz(d.slice(0, 10), orgTz) : '—');

  const facts: [string, string][] = [
    ['Left', `${formatQuantity(m.remaining, m.unit)}${m.is_estimate ? ' (estimate)' : ''}`],
    ['Collected', `${formatQuantity(m.quantity, m.unit)} on ${date(m.collected_on)}`],
    ['By', m.collected_by_name || '—'],
    ['From', materialSource(m)],
    ['Use by', date(m.use_by)],
    ['Kept at', m.location_name || '—'],
    ['Status', MATERIAL_STATUS_LABELS[m.status]],
    ...(m.certificate_scheme || m.certificate_number
      ? [['Certificate', [m.certificate_scheme, m.certificate_number].filter(Boolean).join(' · ')] as [string, string]]
      : []),
    ...(m.kind === 'seed' ? [
      ['Seeds per kg', m.seeds_per_kg != null ? formatQuantity(m.seeds_per_kg) : '—'] as [string, string],
      ...(seeds != null ? [['About', `${seeds.toLocaleString('en-AU')} seeds left`] as [string, string]] : []),
      ['Stratified', m.stratify_start ? `${date(m.stratify_start)} – ${m.stratify_end ? date(m.stratify_end) : 'still going'}` : '—'] as [string, string],
      ['Germination', m.latest_test ? `${Number(m.latest_test.germination_pct)}%` : 'Not tested'] as [string, string],
    ] : []),
  ];

  return (
    <>
      <PageHeader
        title={`${MATERIAL_KIND_LABELS[m.kind]} #${m.id}`}
        subtitle={m.cultivar_name}
        icon={<Scissors className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try { downloadBlob(await nurseryMaterialService.labelsPdf([m.id]), `material-label-${m.id}.pdf`); }
              catch { /* the download simply does not happen */ }
            }}>
              <Printer className="h-4 w-4" /> Print label
            </Button>
            {canOperate && m.status !== 'written_off' && (
              <Button variant="outline" size="sm" onClick={() => open('count')}>Count it</Button>
            )}
            {canOperate && isOpen && (
              <Button variant="outline" size="sm" onClick={() => open('write_off')}>Write off</Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {pastUseBy && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            {`Past its use-by of ${date(m.use_by)}. Use it now, or write it off.`}
          </div>
        )}
        {m.status === 'written_off' && m.written_off_reason && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-700">
            {`Written off: ${m.written_off_reason}`}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {facts.map(([k, v]) => (
            <div key={k} className="bg-white border border-neutral-200 rounded-lg px-3 py-2 min-w-0">
              <div className="text-xs text-neutral-400">{k}</div>
              <div className="text-sm font-medium text-neutral-800 break-words">{v}</div>
            </div>
          ))}
        </div>
        {m.notes && <p className="text-sm text-neutral-700 break-words">{m.notes}</p>}
        {canOperate && mode == null && (
          <Button variant="outline" size="sm" onClick={() => open('edit')}>Edit details</Button>
        )}

        {mode && mode !== 'test' && (
          <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-3">
            {mode === 'count' && (
              <>
                <h3 className="text-base font-semibold text-neutral-800">Count what is left</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="mat-remaining" className="block text-sm font-medium text-neutral-700 mb-1">{`Left (${m.unit})`}</label>
                    <input id="mat-remaining" className={inputCls} type="text" inputMode="decimal" value={remaining}
                      onChange={(e) => setRemaining(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="mat-reason" className="block text-sm font-medium text-neutral-700 mb-1">Why it changed</label>
                    <input id="mat-reason" className={inputCls} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                </div>
              </>
            )}
            {mode === 'write_off' && (
              <>
                <h3 className="text-base font-semibold text-neutral-800">Write it off</h3>
                <p className="text-sm text-neutral-600">{`The ${formatQuantity(m.remaining, m.unit)} left go. It stays on record.`}</p>
                <div>
                  <label htmlFor="mat-writeoff" className="block text-sm font-medium text-neutral-700 mb-1">Why</label>
                  <input id="mat-writeoff" className={inputCls} maxLength={500} value={reason}
                    placeholder="e.g. dried out, past its use-by" onChange={(e) => setReason(e.target.value)} />
                </div>
              </>
            )}
            {mode === 'edit' && (
              <>
                <h3 className="text-base font-semibold text-neutral-800">Edit details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="mat-edit-useby" className="block text-sm font-medium text-neutral-700 mb-1">Use by</label>
                    <input id="mat-edit-useby" className={inputCls} type="date" value={useBy} onChange={(e) => setUseBy(e.target.value)} />
                  </div>
                  {m.kind === 'seed' && (
                    <div>
                      <label htmlFor="mat-edit-strat" className="block text-sm font-medium text-neutral-700 mb-1">Stratification ended</label>
                      <input id="mat-edit-strat" className={inputCls} type="date" value={stratifyEnd} onChange={(e) => setStratifyEnd(e.target.value)} />
                    </div>
                  )}
                  <div>
                    <label htmlFor="mat-edit-scheme" className="block text-sm font-medium text-neutral-700 mb-1">Certificate scheme</label>
                    <input id="mat-edit-scheme" className={inputCls} maxLength={100} value={certScheme} onChange={(e) => setCertScheme(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="mat-edit-number" className="block text-sm font-medium text-neutral-700 mb-1">Certificate number</label>
                    <input id="mat-edit-number" className={inputCls} maxLength={100} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="mat-edit-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                    <textarea id="mat-edit-notes" className={inputCls} rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                </div>
              </>
            )}
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMode(null)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={busy}
                onClick={mode === 'count' ? saveCount : mode === 'write_off' ? saveWriteOff : saveDetails}>
                {busy ? 'Saving…' : mode === 'write_off' ? 'Write off' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {m.kind === 'seed' && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-neutral-800">Germination tests</h3>
              {canOperate && mode !== 'test' && (
                <Button variant="outline" size="sm" onClick={() => { open('test'); setTestedOn(today); setTested(''); setGerminated(''); setDays(''); }}>
                  Add a test
                </Button>
              )}
            </div>
            {mode === 'test' && (
              <div className="border border-primary-200 rounded-lg p-3 mb-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor="test-on" className="block text-xs text-neutral-500 mb-1">Tested on</label>
                    <input id="test-on" className={inputCls} type="date" value={testedOn} onChange={(e) => setTestedOn(e.target.value)} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor="test-method" className="block text-xs text-neutral-500 mb-1">How</label>
                    <select id="test-method" className={inputCls} value={method} onChange={(e) => setMethod(e.target.value as SeedTestMethod)}>
                      {(Object.keys(SEED_TEST_METHOD_LABELS) as SeedTestMethod[]).map((k) => (
                        <option key={k} value={k}>{SEED_TEST_METHOD_LABELS[k]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="test-tested" className="block text-xs text-neutral-500 mb-1">Seeds tested</label>
                    <input id="test-tested" className={inputCls} type="text" inputMode="numeric" value={tested} onChange={(e) => setTested(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="test-germinated" className="block text-xs text-neutral-500 mb-1">Germinated</label>
                    <input id="test-germinated" className={inputCls} type="text" inputMode="numeric" value={germinated} onChange={(e) => setGerminated(e.target.value)} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor="test-days" className="block text-xs text-neutral-500 mb-1">Days (optional)</label>
                    <input id="test-days" className={inputCls} type="text" inputMode="decimal" value={days} onChange={(e) => setDays(e.target.value)} />
                  </div>
                </div>
                {formError && <p className="text-sm text-red-600">{formError}</p>}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setMode(null)}>Cancel</Button>
                  <Button variant="primary" size="sm" onClick={saveTest} disabled={busy}>{busy ? 'Saving…' : 'Save test'}</Button>
                </div>
              </div>
            )}
            {m.tests.length === 0 ? (
              <p className="text-sm text-neutral-500">Not tested yet. A test before sowing says how much to sow.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {[...m.tests].reverse().map((t, i) => (
                  <li key={t.id} className="py-2 flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="text-neutral-800">
                      {`${Number(t.germination_pct)}% · ${t.seeds_germinated} of ${t.seeds_tested}`}
                      {i === 0 && <span className="ml-2 text-xs text-primary-700">latest</span>}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {[date(t.tested_on), SEED_TEST_METHOD_LABELS[t.method],
                        t.days_to_germinate != null ? `${Number(t.days_to_germinate)} days` : null,
                        t.tested_by_name].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-neutral-800 mb-3">History</h3>
          <ol className="space-y-2">
            {m.events.map((e) => (
              <li key={e.id} className="text-sm">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-neutral-800">{EVENT_LABELS[e.event]}</span>
                  <span className="text-neutral-600">
                    {`${Number(e.quantity_delta) > 0 ? '+' : ''}${formatQuantity(e.quantity_delta)}, leaving ${formatQuantity(e.remaining_after, m.unit)}`}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 break-words">
                  {[formatDateTimeInTz(e.recorded_at, orgTz), e.recorded_by_name, e.reason,
                    e.overrun != null ? `${formatQuantity(e.overrun, m.unit)} more than it held` : null].filter(Boolean).join(' · ')}
                </p>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </>
  );
}

export default MaterialDetail;
